import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { toast } from "sonner";
import { ArrowDownUp, Settings2, Zap } from "lucide-react";
import { createTrade } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";

const PRESETS = [25, 50, 75, 100];
const SLIPPAGES = [0.5, 1, 2];

export default function TradePanel({ coin, pair }) {
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState(1);
  const [busy, setBusy] = useState(false);
  const { address, isConnected } = useAccount();
  const { open } = useAppKit();
  const { factory } = useConfig();
  const qc = useQueryClient();

  const price = coin.price_usd || 1e-9;
  const pairUsd = pair?.symbol === "ETH" ? 3000 : pair?.symbol === "cbBTC" ? 95000 : pair?.symbol === "USDC" ? 1 : 5;
  const estTokens = amount ? (parseFloat(amount) * pairUsd) / price : 0;

  const execute = async () => {
    if (!amount || parseFloat(amount) <= 0) return toast.error("Enter an amount");
    if (!isConnected) {
      toast.message("Connect your wallet to trade");
      return open();
    }
    setBusy(true);
    try {
      if (!factory) {
        toast.message("Pool goes live once the v4 factory is deployed", {
          description: "Recording your simulated trade for now.",
        });
      }
      await createTrade(coin.id, {
        side,
        amount_pair: parseFloat(amount),
        amount_token: side === "buy" ? estTokens : parseFloat(amount),
        price_usd: price,
        wallet: address || "",
      });
      qc.invalidateQueries({ queryKey: ["coin", coin.id] });
      qc.invalidateQueries({ queryKey: ["trades", coin.id] });
      qc.invalidateQueries({ queryKey: ["chart", coin.id] });
      toast.success(`${side === "buy" ? "Bought" : "Sold"} $${coin.symbol}`, {
        description: `${amount} ${side === "buy" ? pair?.symbol : coin.symbol}`,
      });
      setAmount("");
    } catch (e) {
      toast.error("Trade failed", { description: e?.message });
    } finally {
      setBusy(false);
    }
  };

  const inSym = side === "buy" ? pair?.symbol : coin.symbol;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#131824]/80 p-4 sm:p-5">
      <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-[#0d111a] p-1">
        <button
          data-testid="trade-buy-tab"
          onClick={() => setSide("buy")}
          className={`rounded-lg py-2.5 text-sm font-bold transition-all ${
            side === "buy" ? "bg-[#00e699] text-[#052015]" : "text-slate-400 hover:text-white"
          }`}
        >
          Buy
        </button>
        <button
          data-testid="trade-sell-tab"
          onClick={() => setSide("sell")}
          className={`rounded-lg py-2.5 text-sm font-bold transition-all ${
            side === "sell" ? "bg-[#ff3b69] text-white" : "text-slate-400 hover:text-white"
          }`}
        >
          Sell
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0d111a] p-3">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>You pay</span>
          <span>{inSym}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <input
            data-testid="trade-amount-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            type="number"
            min="0"
            step="any"
            placeholder="0.0"
            className="w-full bg-transparent font-mono2 text-2xl font-bold text-slate-50 outline-none placeholder:text-slate-700"
          />
          <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#131824] px-2.5 py-1.5">
            <img
              src={side === "buy" ? pair?.logo : coin.image}
              alt=""
              className="h-5 w-5 rounded-full"
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <span className="font-mono2 text-xs font-semibold">{inSym}</span>
          </div>
        </div>
      </div>

      <div className="my-2 flex justify-center">
        <div className="rounded-lg border border-white/10 bg-[#131824] p-1.5 text-slate-400">
          <ArrowDownUp size={14} />
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0d111a] p-3">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>You receive (est.)</span>
          <span>{side === "buy" ? coin.symbol : pair?.symbol}</span>
        </div>
        <div className="mt-1 font-mono2 text-2xl font-bold text-slate-300">
          {side === "buy"
            ? estTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })
            : (amount ? (parseFloat(amount) * price) / pairUsd : 0).toPrecision(4)}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            data-testid={`trade-preset-${p}`}
            onClick={() => setAmount(String((p / 100) * (side === "buy" ? 0.1 : 1000)))}
            className="rounded-lg border border-white/10 bg-[#0d111a] py-1.5 text-xs font-semibold text-slate-400 transition-colors hover:border-blue-500/50 hover:text-white"
          >
            {p}%
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between rounded-xl border border-white/10 bg-[#0d111a] px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <Settings2 size={13} /> Slippage
        </span>
        <div className="flex gap-1">
          {SLIPPAGES.map((s) => (
            <button
              key={s}
              data-testid={`slippage-${s}`}
              onClick={() => setSlippage(s)}
              className={`rounded-md px-2 py-1 text-xs font-semibold ${
                slippage === s ? "bg-blue-500 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {s}%
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid="swap-execute-button"
        onClick={execute}
        disabled={busy}
        className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white disabled:opacity-60 ${
          side === "buy" ? "btn-accent" : "bg-[#ff3b69] hover:bg-[#ff5580]"
        }`}
      >
        <Zap size={16} />
        {busy ? "Processing…" : isConnected ? `${side === "buy" ? "Buy" : "Sell"} $${coin.symbol}` : "Connect wallet"}
      </button>

      <p className="mt-3 text-center text-[11px] text-slate-500">
        Trades route through the coin's Uniswap v4 pool · {slippage}% max slippage
      </p>
    </div>
  );
}
